import React, { Fragment } from 'react';
import styled from 'styled-components';

var lib = {};

var symbols = {};

/**
 * Symbols used internally within ts-pattern to construct and discriminate
 * Guard, Not, and Select, and AnonymousSelect patterns
 *
 * Symbols have the advantage of not appearing in auto-complete suggestions in
 * user defined patterns, and eliminate the admittedly unlikely risk of property
 * overlap between ts-pattern internals and user defined patterns.
 *
 * These symbols have to be visible to tsc for type inference to work, but
 * users should not import them
 * @module
 * @private
 * @internal
 */
Object.defineProperty(symbols, "__esModule", { value: true });
symbols.AnonymousSelectKey = symbols.Select = symbols.Not = symbols.Guard = symbols.PatternKind = void 0;
/** @internal This symbol should only be used by ts-pattern's internals. */
symbols.PatternKind = Symbol('@ts-pattern/pattern-kind');
/** @internal This symbol should only be used by ts-pattern's internals. */
symbols.Guard = Symbol('@ts-pattern/guard');
/** @internal This symbol should only be used by ts-pattern's internals. */
symbols.Not = Symbol('@ts-pattern/not');
/** @internal This symbol should only be used by ts-pattern's internals. */
symbols.Select = Symbol('@ts-pattern/select');
symbols.AnonymousSelectKey = '@ts-pattern/__anonymous-select-key';

var guards = {};

(function (exports) {
Object.defineProperty(exports, "__esModule", { value: true });
exports.instanceOf = exports.select = exports.not = exports.when = void 0;
const symbols$1 = symbols;
const when = (predicate) => ({
    [symbols$1.PatternKind]: symbols$1.Guard,
    [symbols$1.Guard]: predicate,
});
exports.when = when;
const not = (pattern) => ({
    [symbols$1.PatternKind]: symbols$1.Not,
    [symbols$1.Not]: pattern,
});
exports.not = not;
function select(key) {
    return key === undefined
        ? {
            [symbols$1.PatternKind]: symbols$1.Select,
            [symbols$1.Select]: symbols$1.AnonymousSelectKey,
        }
        : {
            [symbols$1.PatternKind]: symbols$1.Select,
            [symbols$1.Select]: key,
        };
}
exports.select = select;
function isInstanceOf(classConstructor) {
    return (val) => val instanceof classConstructor;
}
const instanceOf = (classConstructor) => (0, exports.when)(isInstanceOf(classConstructor));
exports.instanceOf = instanceOf;
}(guards));

var wildcards = {};

Object.defineProperty(wildcards, "__esModule", { value: true });
wildcards.__ = void 0;
const guards_1 = guards;
function isUnknown(x) {
    return true;
}
function isNumber(x) {
    return typeof x === 'number';
}
function numberIsNaN(x) {
    return Number.isNaN(x);
}
function isString(x) {
    return typeof x === 'string';
}
function isBoolean(x) {
    return typeof x === 'boolean';
}
function isNullish(x) {
    return x === null || x === undefined;
}
const unknownGuard = (0, guards_1.when)(isUnknown);
const stringGuard = (0, guards_1.when)(isString);
const numberGuard = (0, guards_1.when)(isNumber);
const NaNGuard = (0, guards_1.when)(numberIsNaN);
const booleanGuard = (0, guards_1.when)(isBoolean);
const nullishGuard = (0, guards_1.when)(isNullish);
/**
 * ### Catch All wildcard
 * `__` is wildcard pattern, matching **any value**.
 *
 * `__.string` is wildcard pattern matching any **string**.
 *
 * `__.number` is wildcard pattern matching any **number**.
 *
 * `__.NaN` is wildcard pattern matching **NaN**
 *
 * `__.boolean` is wildcard pattern matching any **boolean**.
 *
 * `__.nullish` is wildcard pattern matching **null** or **undefined**.
 * @example
 *  match(value)
 *   .with(__, () => 'will always match')
 *   .with(__.string, () => 'will match on strings only')
 *   .with(__.number, () => 'will match on numbers only')
 *   .with(__.NaN, () => 'will match on NaN')
 *   .with(__.boolean, () => 'will match on booleans only')
 *   .with(__.nullish, () => 'will match on null or undefined only')
 */
wildcards.__ = Object.assign(unknownGuard, {
    string: stringGuard,
    number: numberGuard,
    NaN: NaNGuard,
    boolean: booleanGuard,
    nullish: nullishGuard,
});

(function (exports) {
Object.defineProperty(exports, "__esModule", { value: true });
exports.isMatching = exports.match = exports.instanceOf = exports.select = exports.not = exports.when = exports.__ = void 0;
const symbols$1 = symbols;
const guards_1 = guards;
Object.defineProperty(exports, "when", { enumerable: true, get: function () { return guards_1.when; } });
Object.defineProperty(exports, "not", { enumerable: true, get: function () { return guards_1.not; } });
Object.defineProperty(exports, "select", { enumerable: true, get: function () { return guards_1.select; } });
Object.defineProperty(exports, "instanceOf", { enumerable: true, get: function () { return guards_1.instanceOf; } });
const wildcards_1 = wildcards;
Object.defineProperty(exports, "__", { enumerable: true, get: function () { return wildcards_1.__; } });
/**
 * #### match
 *
 * Entry point to create a pattern matching expression.
 *
 * It returns a `Match` builder, on which you can chain
 * several `.with(pattern, handler)` clauses.
 */
const match = (value) => builder(value, []);
exports.match = match;
/**
 * ### builder
 * This is the implementation of our pattern matching, using the
 * builder pattern.
 */
const builder = (value, cases) => {
    const run = () => {
        const entry = cases.find(({ test }) => test(value));
        if (!entry) {
            let displayedValue;
            try {
                displayedValue = JSON.stringify(value);
            }
            catch (e) {
                displayedValue = value;
            }
            throw new Error(`Pattern matching error: no pattern matches value ${displayedValue}`);
        }
        return entry.handler(entry.select(value), value);
    };
    return {
        with(...args) {
            const handler = args[args.length - 1];
            const patterns = [];
            const predicates = [];
            for (let i = 0; i < args.length - 1; i++) {
                const arg = args[i];
                if (typeof arg === 'function') {
                    predicates.push(arg);
                }
                else {
                    patterns.push(arg);
                }
            }
            let selected = {};
            const doesMatch = (value) => Boolean(patterns.some((pattern) => matchPattern(pattern, value, (key, value) => {
                selected[key] = value;
            })) && predicates.every((predicate) => predicate(value)));
            return builder(value, cases.concat([
                {
                    test: doesMatch,
                    handler,
                    select: (value) => Object.keys(selected).length
                        ? symbols$1.AnonymousSelectKey in selected
                            ? selected[symbols$1.AnonymousSelectKey]
                            : selected
                        : value,
                },
            ]));
        },
        when: (predicate, handler) => builder(value, cases.concat([
            {
                test: predicate,
                handler,
                select: (value) => value,
            },
        ])),
        otherwise: (handler) => builder(value, cases.concat([
            {
                test: () => true,
                handler,
                select: (value) => value,
            },
        ])).run(),
        exhaustive: () => run(),
        run,
    };
};
const isObject = (value) => Boolean(value && typeof value === 'object');
const isGuardPattern = (x) => {
    const pattern = x;
    return pattern && pattern[symbols$1.PatternKind] === symbols$1.Guard;
};
const isNotPattern = (x) => {
    const pattern = x;
    return pattern && pattern[symbols$1.PatternKind] === symbols$1.Not;
};
const isSelectPattern = (x) => {
    const pattern = x;
    return pattern && pattern[symbols$1.PatternKind] === symbols$1.Select;
};
// tells us if the value matches a given pattern.
const matchPattern = (pattern, value, select) => {
    if (isObject(pattern)) {
        if (isGuardPattern(pattern))
            return Boolean(pattern[symbols$1.Guard](value));
        if (isSelectPattern(pattern)) {
            select(pattern[symbols$1.Select], value);
            return true;
        }
        if (isNotPattern(pattern))
            return !matchPattern(pattern[symbols$1.Not], value, select);
        if (!isObject(value))
            return false;
        if (Array.isArray(pattern)) {
            if (!Array.isArray(value))
                return false;
            // List pattern
            if (pattern.length === 1) {
                const selected = {};
                const listSelect = (key, value) => {
                    selected[key] = (selected[key] || []).concat([value]);
                };
                const doesMatch = value.every((v) => matchPattern(pattern[0], v, listSelect));
                if (doesMatch) {
                    Object.keys(selected).forEach((key) => select(key, selected[key]));
                }
                return doesMatch;
            }
            // Tuple pattern
            return pattern.length === value.length
                ? pattern.every((subPattern, i) => matchPattern(subPattern, value[i], select))
                : false;
        }
        if (pattern instanceof Map) {
            if (!(value instanceof Map))
                return false;
            return [...pattern.keys()].every((key) => matchPattern(pattern.get(key), value.get(key), select));
        }
        if (pattern instanceof Set) {
            if (!(value instanceof Set))
                return false;
            if (pattern.size === 0)
                return value.size === 0;
            if (pattern.size === 1) {
                const [subPattern] = [...pattern.values()];
                return Object.values(wildcards_1.__).includes(subPattern)
                    ? matchPattern([subPattern], [...value.values()], select)
                    : value.has(subPattern);
            }
            return [...pattern.values()].every((subPattern) => value.has(subPattern));
        }
        return Object.keys(pattern).every((k) => k in value &&
            matchPattern(
            // @ts-ignore
            pattern[k], 
            // @ts-ignore
            value[k], select));
    }
    return value === pattern;
};
function isMatching(...args) {
    if (args.length === 1) {
        const [pattern] = args;
        return (value) => matchPattern(pattern, value, () => { });
    }
    if (args.length === 2) {
        const [pattern, value] = args;
        return matchPattern(pattern, value, () => { });
    }
    throw new Error(`isMatching wasn't given enough arguments: expected 1 or 2, received ${args.length}.`);
}
exports.isMatching = isMatching;
}(lib));

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || from);
}

function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

var directionToFlexDirection = function (direction) {
    return lib.match(direction)
        .with('vertical', function () { return 'column'; })
        .with('horizontal', function () { return 'row'; })
        .exhaustive();
};
var directionToRelativeDirection = function (direction) {
    return lib.match(direction)
        .with('vertical', function () { return 'right'; })
        .with('horizontal', function () { return 'bottom'; })
        .exhaustive();
};
var Wrapper = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  margin: 0;\n  padding: 0;\n  list-style: none;\n\n  display: flex;\n  width: 100%;\n  flex-direction: ", ";\n  align-items: ", ";\n  justify-content: ", ";\n  flex-wrap: ", ";\n  gap: ", "px;\n"], ["\n  margin: 0;\n  padding: 0;\n  list-style: none;\n\n  display: flex;\n  width: 100%;\n  flex-direction: ", ";\n  align-items: ", ";\n  justify-content: ", ";\n  flex-wrap: ", ";\n  gap: ", "px;\n"])), function (p) { return directionToFlexDirection(p.direction); }, function (p) {
    return lib.match(p.alignItems)
        .with('start', function () { return 'flex-start'; })
        .with('center', 'stretch', function (v) { return v; })
        .exhaustive();
}, function (p) {
    return lib.match(p.justifyContent)
        .with('start', function () { return 'flex-start'; })
        .with('center', 'space-between', function (v) { return v; })
        .exhaustive();
}, function (p) { return (p.allowWrap ? 'wrap' : 'nowrap'); }, function (p) { return p.spacing; });
var ChildElement = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  flex-grow: ", ";\n"], ["\n  flex-grow: ", ";\n"])), function (p) { return p.flexGrow; });
var Divider = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  align-self: stretch;\n  border-", ":\n    1px solid ", ";\n"], ["\n  align-self: stretch;\n  border-", ":\n    1px solid ", ";\n"])), function (p) { return directionToRelativeDirection(p.direction); }, function (p) { return p.theme.colors.background_dark; });
var templateObject_1, templateObject_2, templateObject_3;

var isReactFragment = function (child) {
    return child.type === React.Fragment;
};
/** Inspired by: https://github.com/gregberge/react-flatten-children/blob/master/src/index.tsx */
var flattenChildren = function (children) {
    return React.Children.toArray(children).reduce(function (flatChildren, child) { return __spreadArray(__spreadArray([], flatChildren, true), (isReactFragment(child)
        ? flattenChildren(child.props.children)
        : [child]), true); }, []);
};

var Stack = function (_a) {
    var _b = _a.direction, direction = _b === void 0 ? 'horizontal' : _b, _c = _a.spacing, spacing = _c === void 0 ? 0 : _c, _d = _a.alignItems, alignItems = _d === void 0 ? 'center' : _d, _e = _a.justifyContent, justifyContent = _e === void 0 ? 'start' : _e, _f = _a.growChildren, growChildren = _f === void 0 ? 'none' : _f, _g = _a.allowWrap, allowWrap = _g === void 0 ? true : _g, _h = _a.divider, divider = _h === void 0 ? false : _h, _j = _a.asList, asList = _j === void 0 ? false : _j, children = _a.children;
    var childrenArray = flattenChildren(children);
    var flexGrows = lib.match(growChildren)
        .with('none', function () { return []; })
        .with('all', function () { return Array(childrenArray.length).fill(1); })
        .with([lib.__.number], function (v) { return v; })
        .exhaustive();
    return (React.createElement(Wrapper, { as: asList ? 'ul' : 'div', direction: direction, spacing: spacing, alignItems: alignItems, justifyContent: justifyContent, allowWrap: allowWrap }, childrenArray.map(function (child, i, childrenArray) { return (React.createElement(Fragment, { key: i },
        React.createElement(ChildElement, { as: asList ? 'li' : 'div', flexGrow: flexGrows[i] }, child),
        divider && i < childrenArray.length - 1 && (React.createElement(Divider, { "aria-hidden": true, direction: lib.match(direction)
                .with('vertical', function () { return 'horizontal'; })
                .with('horizontal', function () { return 'vertical'; })
                .exhaustive() })))); })));
};
Stack.displayName = 'Stack';

export { Stack as default };
//# sourceMappingURL=index.esm.js.map
